import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Check, CreditCard, FileBarChart, FileText, Hammer, Home, IdCard, Landmark, ListOrdered, Loader2, Presentation, Receipt, Upload, X } from "lucide-react";
import { fastDocsFor } from "@/lib/fastDocs";
import { FastMark } from "@/components/portal/crm/FastTrackTag";
import "@/styles/fast-docs.css";

const ICONS = { bank: Landmark, chart: FileBarChart, id: IdCard, list: ListOrdered, card: CreditCard, home: Home, works: Hammer, receipt: Receipt, deck: Presentation, doc: FileText };
const PACE = ["Standard", "Quicker", "Quicker still", "Fast track"];
const ACCEPT = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt";

function SpeedTrack({ got }) {
  return (
    <div className="fd-track" aria-label={`${got} of 3 in — ${PACE[got]}`}>
      <div className="fd-bar"><i style={{ width: `${(got / 3) * 100}%` }} /></div>
      <div className="fd-pace">
        <span>Speed to a decision</span>
        <b data-testid="fast-docs-pace">{PACE[got]}</b>
      </div>
    </div>
  );
}

function Slot({ doc, i, files, onUpload, onRemove, testId }) {
  const ref = useRef(null);
  const [pct, setPct] = useState(null);
  const [over, setOver] = useState(false);
  const Icon = ICONS[doc.icon] || FileText;
  const done = files.length > 0;

  const take = async (list) => {
    for (const file of Array.from(list || [])) {
      if (file.size > 75 * 1024 * 1024) { toast.error(`${file.name} is over the 75MB limit`); continue; }
      setPct(0);
      try { await onUpload(doc.label, file, setPct); toast.success(`${doc.label} — in`); }
      catch (e) { toast.error(e?.response?.data?.detail || `${file.name} didn't upload — try again`); }
    }
    setPct(null);
  };

  return (
    <li className={`fd-slot${done ? " is-done" : ""}${over ? " is-over" : ""}`} style={{ "--i": i }} data-testid={testId}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}>
      <span className="fd-ico">{done ? <Check className="size-4" strokeWidth={3} /> : <Icon className="size-4" />}</span>
      <div className="fd-txt">
        <b>{doc.label}</b>
        <small data-testid={`${testId}-state`}>{done ? files.map((f) => f.filename).join(", ") : doc.why}</small>
        {pct !== null && <span className="fd-prog"><i style={{ width: `${pct}%` }} /></span>}
      </div>
      {done && onRemove && files[0]?.id && (
        <button type="button" className="fd-x" aria-label={`Remove ${doc.label}`} onClick={() => files.forEach((f) => onRemove(f.id))}
                data-testid={`${testId}-remove`}><X className="size-3.5" /></button>
      )}
      <button type="button" className="fd-btn" onClick={() => ref.current?.click()} disabled={pct !== null} data-testid={`${testId}-upload`}>
        {pct !== null ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
        {pct !== null ? `${pct}%` : done ? "Add more" : "Upload"}
      </button>
      <input ref={ref} type="file" multiple hidden accept={ACCEPT} data-testid={`${testId}-input`}
             onChange={(e) => { take(e.target.files); e.target.value = ""; }} />
    </li>
  );
}

/** "Get funded faster" — the three documents lenders ask for first, each with its own upload slot. Always optional. */
export function FastDocs({ financeType, files = [], onUpload, onRemove, foot, testId = "fast-docs" }) {
  const docs = fastDocsFor(financeType);
  const inSlot = (d) => files.filter((f) => f.label === d.label);
  const got = docs.filter((d) => inSlot(d).length).length;
  const all = got === docs.length;
  return (
    <section className={`fd${all ? " is-all" : ""}`} data-testid={testId}>
      <header className="fd-head">
        <span className="fd-bolt" aria-hidden="true"><FastMark size={26} /></span>
        <div>
          <div className="fd-eyebrow">Get funded faster</div>
          <h3 className="fd-title" data-testid={`${testId}-title`}>{all ? "You're on the fast track" : "The three documents lenders ask for first"}</h3>
          <p className="fd-sub">{all ? "Everything a lender needs to get started is here — nothing to wait on."
            : "Send them now and a lender can start straight away — no waiting on emails back and forth."}</p>
        </div>
        <span className="fd-count" data-testid={`${testId}-count`}>{got} of {docs.length}</span>
      </header>
      <SpeedTrack got={got} />
      <ul className="fd-list">
        {docs.map((d, i) => <Slot key={d.label} doc={d} i={i} files={inSlot(d)} onUpload={onUpload} onRemove={onRemove} testId={`${testId}-slot-${i}`} />)}
      </ul>
      <p className="fd-foot">{foot || "Optional — don't have them to hand? Carry on, and we'll send you a secure link to add them later."}</p>
    </section>
  );
}

export default FastDocs;
